import {getLocationQueryObject} from "./utils/text";
import clientStorage from "./utils/client-storage";
import {detect} from "detect-browser";
import {generateHash} from "./utils/text";
import {androidRequestParams, iosRequestParams} from "./utils/constants";
import {isTokenSymbol, isWebviewApp, isWebviewCountriesOf, isWebviewCountryOf} from "./utils/config.utils";
import {webviewAppIds} from "./utils/webview";
import {webviewContext} from "./utils/webview-api/context";
import {webviewGetWebApiInfo, webviewGetWebApiInfoIsSupport} from "./utils/webview-api/get-web-api-info";

if (!clientStorage.hasFirstVisitAt()) {
  clientStorage.setFirstVisitAt(Date.now());
}

let query = getLocationQueryObject();
const platformInfo = detect();
const platformIsMobile = ["android", "android os", "ios"].indexOf(platformInfo.os.toLowerCase()) > -1;
let isWebview = typeof query["aid"] !== "undefined" || typeof query["vicman_unified_id"] !== "undefined";
let isWebviewMock = false;

if (isWebview) {
  clientStorage.setWebviewRequestParams(query);
}

const webviewHostNames = (process.env.REACT_APP_WEBVIEW_HOSTNAMES || "").split(",");

if (!isWebview && webviewHostNames.includes(window.location.hostname)) {
  let requestParams = clientStorage.getWebviewRequestParams();

  isWebview = true;
  isWebviewMock = true;

  if (Object.keys(requestParams).length === 0) {
    if (["ios"].indexOf(platformInfo.os.toLowerCase()) > -1) {
      requestParams = iosRequestParams;
      requestParams.vicman_unified_id = generateHash();
    } else {
      requestParams = androidRequestParams;
      requestParams.aid = generateHash();
    }

    clientStorage.setWebviewRequestParams(requestParams);
  }

  query = {...requestParams, ...query};
}

window.clientConfig = {
  platform: platformInfo,
  isWeb: !isWebview,
  isWebMobile: !isWebview && platformIsMobile,
  isWebMobileDesktopMode: false,
  isWebMobileAndroid: platformInfo.os.toLowerCase() === "android" || platformInfo.os.toLowerCase() === "android os",
  isWebMobileIOS: platformInfo.os.toLowerCase() === "ios",
  isWebDesktop: !isWebview && !platformIsMobile,
  isWebview: isWebview,
  isWebviewAndroid: typeof query["aid"] !== "undefined",
  isWebviewIOS: typeof query["vicman_unified_id"] !== "undefined",
  isWebviewMock: isWebviewMock,
  isAnyMobile: isWebview || platformIsMobile,
  isAnyIOS: platformInfo.os.toLowerCase() === "ios",
  isAnyAndroid: platformInfo.os.toLowerCase().indexOf("android") > -1,
  webviewParams: isWebview ? query : {},
  isPro: query["is_pro"] === "1",
  isTrial: query["subs_is_trial"] === "1",
  isCancelledTrial: query["subs_is_trial"] === "1" && query["subs_state"] === "cancelled",
  subscriptionSku: query["subs_id"],
  locale: query["locale"] || query["lang"] || clientStorage.getSelectedLang() || window.navigator.language || undefined,
  splitGroupId: undefined,
  loadedUrlParams: query,
  isNew: Date.now() < (clientStorage.getFirstVisitAt() + 43_200_000), // 12h
  geoipCountryCode: null,
  currentProcessingText: 0,
  isPaidTrafficUser: (typeof query["af_campaign"] === "string" && query["af_campaign"].length > 0) || query["is_attr_user"] === "1",
};

window.clientConfig.isUsaUser = isWebviewCountryOf("us", true);
window.clientConfig.isBrazilianUser = isWebviewCountryOf("br");
window.clientConfig.isTurkeyUser = isWebviewCountryOf("tr");
window.clientConfig.isIndianUser = isWebviewCountryOf("in");
window.clientConfig.isIndiaRegionUser = isWebviewCountriesOf(["in", "pk", "bd"]);

if (isWebviewApp(webviewAppIds.photolabProHD, webviewAppIds.photolabProIos)) {
  window.clientConfig.isPro = true;
}

window.clientConfig.lang = window.clientConfig.locale
  ? window.clientConfig.locale.substring(0, 2).toLowerCase()
  : undefined;

window.clientConfig.isWebMobileDesktopMode = (function(){
  const webkitVer = parseInt(/WebKit\/(\d+)|$/.exec(window.navigator.appVersion || "")[1], 10); // also matches AppleWebKit
  const isGoogle = webkitVer && window.navigator.vendor.indexOf("Google") === 0;  // Also true for Opera Mobile and maybe others
  const isAndroid = isGoogle && window.navigator.userAgent.indexOf("Android") > 0;  // Careful - Firefox and Windows Mobile also have Android in user agent
  return !isAndroid
    && isGoogle
    && ((window.navigator.platform || "").indexOf('Linux a') === 0)
    && "ontouchstart" in window.document.documentElement;
})();

if (query["mock_mobile_desktop_mode"] === "1") {
  window.clientConfig.isWebMobileDesktopMode = true;
}

// ---

webviewContext({
  platform: window.clientConfig.isWebviewAndroid ? "android" : (window.clientConfig.isWebviewIOS ? "ios" : "unrecognized"),
  appId: parseInt(query["app_id"]) || 0,
  versionCode: parseInt(query["version_code"]) || 0,
});

window.clientConfig.webviewContextReady = window.clientConfig.isWebview
  ? fetchWebviewApiVersion()
  : Promise.resolve();

async function fetchWebviewApiVersion() {
  if (!webviewGetWebApiInfoIsSupport()) {
    return;
  }

  try {
    const response = await Promise.race([
      webviewGetWebApiInfo(),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000)),
    ]);

    if (response?.ok) {
      webviewContext({webApiVersion: parseInt(response.result.web_api_version) || 0});
    }
  } catch (error) {
    console.warn("[webview-api] getWebApiInfo failed or timed out", error);
  }
}

// ---

// if (window.clientConfig.isWebview) {
//   window.clientConfig.isNew = parseInt(window.clientConfig.webviewParams["session_idx"] || 1) <= 1;
// }

window.clientConfig.isOld = !window.clientConfig.isNew;

// ---

if (window.clientConfig.isWebview) {
  window.clientConfig.token = window.clientConfig.isWebviewAndroid
    ? `w:a:${query["aid"]}`
    : `w:i:${query["vicman_unified_id"]}`;
} else {
  const clientToken = clientStorage.getClientToken();
  if (clientToken) {
    window.clientConfig.token = clientToken;
  } else {
    const type = platformIsMobile ? "m" : "d";
    const hash = generateHash();

    window.clientConfig.token = `b:${type}:${hash}`;
    clientStorage.setClientToken(window.clientConfig.token);
  }
}

// ---

if (window.clientConfig.isWebview) {
  const aid = query["aid"] || query["vicman_unified_id"];
  const num = parseInt(aid.substring(aid.length - 2), 16);
  const group = Math.floor(num / (255 / 10)) + 1;

  window.clientConfig.splitGroupId = Math.min(group, 10); // fix for ff => 11 group
} else {
  const groupId = query["split_group"] || clientStorage.getSplitGroupId();
  if (groupId) {
    window.clientConfig.splitGroupId = parseInt(groupId);
  } else {
    window.clientConfig.splitGroupId = Math.floor(Math.random() * 10) + 1;
  }

  clientStorage.setSplitGroupId(window.clientConfig.splitGroupId);
}

// ---

window.appConfig = {

  project: {
    name: process.env.REACT_APP_PROJECT_NAME || "",
    key: process.env.REACT_APP_PROJECT_KEY || "",
  },

  isTestInstance: process.env.REACT_APP_TEST_INSTANCE === "true",
  isProdMode: process.env.NODE_ENV.indexOf("prod") === 0,
  isDebug: process.env.NODE_ENV.indexOf("prod") !== 0
    || process.env.REACT_APP_DEBUG === "true"
    || query["vicman_debug"]
    || false,
  isTesterMode: window.clientConfig.isWebview && isTokenSymbol(1, "x"),
  isBarbifyMe: window.location.hostname === "barbify.me",

  build: process.env.REACT_APP_BUILD_INFO || {},

  paths: {
    app: process.env.REACT_APP_APP_PATH,
    assets: process.env.REACT_APP_ASSETS_PATH,
    pwAssets: process.env.REACT_APP_PW_ASSETS_PATH,
    api: process.env.REACT_APP_API_PATH,
    apiUpload: process.env.REACT_APP_API_FILES_PATH,
    apiSign: process.env.REACT_APP_API_SIGN_PATH,
    proxy: process.env.REACT_APP_PW_PROXY_PATH,
    stat: process.env.REACT_APP_STAT_PATH,
    tempImagesUploadEndpoint: process.env.REACT_APP_TEMP_IMAGES_UPLOAD_ENDPOINT,
    inapps: process.env.REACT_APP_INAPPS_ENDPOINT,
    moderateImages: process.env.REACT_APP_MODERATE_IMAGES_ENDPOINT,
    faceDetails: process.env.REACT_APP_FACE_DETAILS_ENDPOINT,
    imagesStore: process.env.REACT_APP_IMAGES_STORE_ENDPOINT,
    imageToVideo: process.env.REACT_APP_IMAGE_TO_VIDEO_ENDPOINT,
  },

  photolab: {
    appId: process.env.REACT_APP_PHOTOLAB_APP_ID,
    path: process.env.REACT_APP_PHOTOLAB_API_PATH,
    addTaskEndpoint: process.env.REACT_APP_PHOTOLAB_ADDTASK_ENDPOINT,
  },

  analytics: {
    isEnabled: process.env.REACT_APP_ANALYTICS_ENABLED === "true",
    endpoint: process.env.REACT_APP_ANALYTICS_ENDPOINT,
    allowedUserGroups: envAsArrayOfNumbers(process.env.REACT_APP_ANALYTICS_ALLOWED_USER_GROUPS || ""),
  },

  processingTimings: {
    isEnabled: process.env.REACT_APP_PROCESSING_TIMINGS_ENABLED === "true",
    endpoint: process.env.REACT_APP_PROCESSING_TIMINGS_ENDPOINT,
  },

  hits: {
    isEnabled: process.env.REACT_APP_HITS_ENABLED === "true",
    endpoint: process.env.REACT_APP_HITS_ENDPOINT,
    allowedUserGroups: envAsArrayOfNumbers(process.env.REACT_APP_HITS_ALLOWED_USER_GROUPS || ""),
    increment: parseInt(process.env.REACT_APP_HITS_INCREMENT || 1),
  },

  signals: {
    isEnabled: process.env.REACT_APP_SIGNALS_ENABLED === "true",
    endpoint: process.env.REACT_APP_SIGNALS_ENDPOINT,
    allowedUserGroups: envAsArrayOfNumbers(process.env.REACT_APP_SIGNALS_ALLOWED_USER_GROUPS || ""),
  },

  sentry: {
    isEnabled: envAsBoolean(process.env.REACT_APP_SENTRY_ENABLED),
    dsn: process.env.REACT_APP_SENTRY_DSN || "",
    sampleRate: parseFloat(process.env.REACT_APP_SENTRY_SAMPLE_RATE || 0),
  },

  googleAnalytics: {
    isEnabled: (process.env.REACT_APP_GOOGLE_ANALYTICS_ENABLED === "true") && window.clientConfig.isWeb,
    trackerId: process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKER_ID,
  },

  nativeAds: {
    isEnabled: process.env.REACT_APP_NATIVE_ADS_IS_ENABLED === "true",
  },

  maskEditor: {
    isEnabledInWeb: process.env.REACT_APP_MASK_EDITOR_ENABLED_WEB === "true",
    isEnabledInWebview: process.env.REACT_APP_MASK_EDITOR_ENABLED_WEBVIEW === "true",
  },

  webview: {
    rateAppDialogPercent: parseFloat(process.env.REACT_APP_WEBVIEW_RATEAPP_DIALOG_PERCENT),
  },

  processings: {
    latestVersion: 3,

    allowOnWeb: envAsBoolean(process.env.REACT_APP_PROCESSINGS_ALLOW_ON_WEB),

    timeout: parseInt(process.env.REACT_APP_PROCESSINGS_TIMEOUT),
    creativeTimeout: parseInt(process.env.REACT_APP_PROCESSINGS_CREATIVE_TIMEOUT),
    creativeStatusNewDuration: parseInt(process.env.REACT_APP_CREATIVE_STATUS_NEW_DURATION) || 0,
    errorButtonTimeout: parseInt(process.env.REACT_APP_PROCESSINGS_ERROR_BUTTON_TIMEOUT),

    creativesRollout: {
      at: envAsDateFromDateString(process.env.REACT_APP_PROCESSINGS_CREATIVES_ROLLOUT_AT) || 0,
      heatingOffset: parseInt(process.env.REACT_APP_PROCESSINGS_CREATIVES_ROLLOUT_HEATING_OFFSET) || 0,
    },

    config: {
      rolloutIsEnabled: envAsBoolean(process.env.REACT_APP_PROCESSINGS_CONFIG_ROLLOUT_ENABLED),
      rolloutInterval: parseInt(process.env.REACT_APP_PROCESSINGS_CONFIG_ROLLOUT_INTERVAL) * 60_000,
      useOldOnUserGroups: envAsArrayOfNumbers(process.env.REACT_APP_PROCESSINGS_CONFIG_OLD_CONFIG_USERGROUPS || ""),
    },

    mobileDesktopModePhotosLimit: parseInt(process.env.REACT_APP_PROCESSINGS_MOBILE_DESKTOP_MODE_PHOTOS_LIMIT || "0"),
    mobilePhotosLimit: parseInt(process.env.REACT_APP_PROCESSINGS_MOBILE_PHOTOS_LIMIT || "0"),
  },

  resultsCollect: {
    isEnabled: envAsBoolean(process.env.REACT_APP_RESULTS_COLLECT_IS_ENABLED),
    endpoint: process.env.REACT_APP_RESULTS_COLLECT_ENDPOINT,
    allowedUserGroups: envAsArrayOfNumbers(process.env.REACT_APP_RESULTS_COLLECT_ALLOWED_USER_GROUPS || ""),
  }
};

function envAsBoolean(input) {
  return input === "true";
}

function envAsArrayOfNumbers(input, delimiter = ",") {
  return input
    .split(delimiter)
    .map((item) => parseInt(item))
    .filter((item) => !isNaN(item));
}

function envAsDateFromDateString(input) {
  return new Date(input);
}
